import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import API from '@/api/api';

export function useProjectSitesData(projectId, projectCode, sitePgnm) {
  const [sites, setSites] = useState([]);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [optionalDocuments, setOptionalDocuments] = useState([]);
  const [attachmentColumnWidth, setAttachmentColumnWidth] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentSiteTablePage, setCurrentSiteTablePage] = useState(1);

  const fetchData = useCallback(async (signal) => {
    try {
      setLoading(true);

      // 프로젝트 참여 사업장 리스트 조회
      const sitesResponse = await API.get(
        `/admin/estimates/projects/${projectId}?project_code=${projectCode}`,
        { signal }
      );
      const sitesData = sitesResponse.data || [];

      // 프로젝트 문서 조회
      const documentsResponse = await API.get('projects/documents', {
        params: { project_code: projectCode },
        signal,
      });
      const documentsData = documentsResponse.data || [];

      if (documentsData.length) {
        const required = documentsData
          .filter(({ is_required }) => is_required === true)
          .map((doc) => ({
            id: doc.id,
            document_type_id: doc.document_type_id,
            document_type: doc.document_type.type_code,
            key: doc.document_type.type_code,
            type_name_ko: doc.document_type.type_name_ko,
            is_required: doc.is_required,
            order_index: doc.order_index,
          }));

        const optional = documentsData
          .filter(({ is_required }) => is_required === false)
          .map((doc) => ({
            id: doc.id,
            document_type_id: doc.document_type_id,
            document_type: doc.document_type.type_code,
            key: doc.document_type.type_code,
            type_name_ko: doc.document_type.type_name_ko,
            is_required: doc.is_required,
            order_index: doc.order_index,
          }));

        setRequiredDocuments(required);
        setOptionalDocuments(optional);

        const totalDocs = required.length + optional.length;
        if (totalDocs > 0) {
          setAttachmentColumnWidth(`${45 / totalDocs}%`);
        }
      }

      setSites(sitesData);

      if (typeof sitePgnm !== 'undefined' && sitePgnm) {
        setCurrentSiteTablePage(parseInt(sitePgnm, 10) || 1);
      }
    } catch (error) {
      if (error.name !== 'CanceledError') {
        message.error('프로젝트 참여 사업장 목록 조회 실패');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId, projectCode, sitePgnm]);

  useEffect(() => {
    const controller = new AbortController();
    if (projectId || projectCode) {
      fetchData(controller.signal);
    }
    return () => {
      controller.abort();
    };
  }, [projectId, projectCode, fetchData]);

  return {
    sites,
    requiredDocuments,
    optionalDocuments,
    attachmentColumnWidth,
    loading,
    currentSiteTablePage,
    setCurrentSiteTablePage,
    refetch: fetchData, // refetch 함수로 fetchData 노출
  };
}
